import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useUserRole } from '@/hooks/useUserRole'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { StickyTableScrollbar } from '@/components/ui/StickyTableScrollbar'
import { showApiError, showValidationMessage } from '@/lib/utils/notifications'
import { PageHeader } from '@/components/layout/PageHeader'
import { ReimbursementsFilters } from '../components/ReimbursementsFilters'
import { ReimbursementsTable } from '../components/ReimbursementsTable'
import { ReimbursementsGrid } from '../components/ReimbursementsGrid'
import { ReimbursementsTotalsRow } from '../components/ReimbursementsTotalsRow'
import { PolicyDeclarationFilesPopup } from '../components/PolicyDeclarationFilesPopup'
import { ListExportStatusCard } from '../components/ListExportStatusCard'
import { useReimbursementsList } from '../hooks/useReimbursementsList'
import { useExportPolling } from '../hooks/useExportPolling'
import {
  LIBERTY_ROLES,
  PER_PAGE_OPTIONS,
  REIMBURSEMENTS_COLUMN_VISIBILITY_STORAGE_KEY,
  REIMBURSEMENTS_LIST_COLUMN_OPTIONS,
  REIMBURSEMENTS_LIST_CUSTOMIZABLE_COLUMN_OPTIONS,
  REIMBURSEMENTS_LIST_DATA_COLUMN_IDS,
  REIMBURSEMENTS_LIST_LOCKED_COLUMN_IDS,
} from '../constants'
import {
  isReimbursementListLockedColumn,
  withLockedReimbursementListColumns,
} from '../utils/reimbursementListColumns'
import { getPolicyDeclarationFiles } from '../services/reimbursementsService'
import {
  buildStatementPath,
  openServeFileInNewTab,
} from '../utils/downloadHelpers'
import { countActiveFilters } from '../utils/activeFilterCount'
import type {
  PolicyDeclarationFile,
  ReimbursementColumnSettings,
  ReimbursementListColumnId,
  ReimbursementListItem,
} from '../types'

const DEFAULT_REIMBURSEMENT_COLUMN_IDS =
  REIMBURSEMENTS_LIST_DATA_COLUMN_IDS as ReimbursementListColumnId[]
const DEFAULT_CUSTOMIZABLE_COLUMN_IDS = REIMBURSEMENTS_LIST_CUSTOMIZABLE_COLUMN_OPTIONS.map(
  (column) => column.id,
)

const LEGACY_REIMBURSEMENT_LIST_COLUMN_IDS: Record<string, ReimbursementListColumnId> = {
  rental_status: 'reimbursement_rental_status',
  rental_progress: 'reimbursement_rental_progress',
}

function migrateLegacyListColumnId(columnId: string): string {
  return LEGACY_REIMBURSEMENT_LIST_COLUMN_IDS[columnId] ?? columnId
}

function normalizeColumnIds(columnIds: unknown): ReimbursementListColumnId[] {
  if (!Array.isArray(columnIds)) return DEFAULT_REIMBURSEMENT_COLUMN_IDS

  const knownColumnIds = new Set(DEFAULT_REIMBURSEMENT_COLUMN_IDS)
  const parsedColumnIds = columnIds
    .filter((columnId): columnId is string => typeof columnId === 'string')
    .map(migrateLegacyListColumnId)
    .filter(
      (columnId): columnId is ReimbursementListColumnId =>
        knownColumnIds.has(columnId as ReimbursementListColumnId),
    )

  const missingColumnIds = DEFAULT_REIMBURSEMENT_COLUMN_IDS.filter(
    (columnId) => !parsedColumnIds.includes(columnId),
  )

  return [...parsedColumnIds, ...missingColumnIds]
}

function loadReimbursementColumnSettings(): ReimbursementColumnSettings {
  const defaultSettings = {
    visibleColumnIds: DEFAULT_REIMBURSEMENT_COLUMN_IDS,
    columnOrderIds: DEFAULT_REIMBURSEMENT_COLUMN_IDS,
  }

  if (typeof window === 'undefined') return defaultSettings

  try {
    const storedValue = window.localStorage.getItem(
      REIMBURSEMENTS_COLUMN_VISIBILITY_STORAGE_KEY,
    )
    if (!storedValue) return defaultSettings

    const parsedValue = JSON.parse(storedValue)
    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
      return defaultSettings
    }

    const parsedSettings = parsedValue as Partial<ReimbursementColumnSettings>

    return {
      visibleColumnIds: withLockedReimbursementListColumns(
        DEFAULT_REIMBURSEMENT_COLUMN_IDS.filter((columnId) =>
          parsedSettings.visibleColumnIds?.includes(columnId),
        ),
      ),
      columnOrderIds: normalizeColumnIds(parsedSettings.columnOrderIds),
    }
  } catch {
    return defaultSettings
  }
}

export function ReimbursementsListPage() {
  const isMobile = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    items,
    totals,
    pagination,
    loading,
    filters,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    statusLabelMaps,
  } = useReimbursementsList()
  const exportPolling = useExportPolling()

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [processingMap, setProcessingMap] = useState<Record<string, boolean>>({})
  const [policyFilesPopup, setPolicyFilesPopup] = useState<PolicyDeclarationFile[] | null>(null)

  const { roleName, orgId, hasPolicy } = useUserRole()
  const [isColumnsOpen, setIsColumnsOpen] = useState(false)
  const [columnSearchTerm, setColumnSearchTerm] = useState('')
  const [draggedColumnId, setDraggedColumnId] =
    useState<ReimbursementListColumnId | null>(null)
  const [dragPreviewColumnOrderIds, setDragPreviewColumnOrderIds] =
    useState<ReimbursementListColumnId[] | null>(null)
  const [columnSettings, setColumnSettings] = useState<ReimbursementColumnSettings>(
    loadReimbursementColumnSettings,
  )
  const { visibleColumnIds, columnOrderIds } = columnSettings
  const dropdownColumnOrderIds = dragPreviewColumnOrderIds ?? columnOrderIds
  const columnsMenuRef = useRef<HTMLDivElement | null>(null)
  const columnsListRef = useRef<HTMLDivElement | null>(null)
  /** Application-list parity: sticky viewport scrollbar for wide table overflow. */
  const resultsScrollRef = useRef<HTMLDivElement | null>(null)

  const activeFiltersCount = useMemo(() => countActiveFilters(filters), [filters])

  useEffect(() => {
    window.localStorage.setItem(
      REIMBURSEMENTS_COLUMN_VISIBILITY_STORAGE_KEY,
      JSON.stringify(columnSettings),
    )
  }, [columnSettings])

  useEffect(() => {
    if (!isColumnsOpen) return

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        columnsMenuRef.current &&
        !columnsMenuRef.current.contains(event.target as Node)
      ) {
        setIsColumnsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsColumnsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isColumnsOpen])

  const availableColumnOptions = useMemo(() => {
    const isLibertyRole = roleName
      ? LIBERTY_ROLES.includes(roleName as (typeof LIBERTY_ROLES)[number])
      : false
    const isLibertyOrg = Number(orgId) === 1
    const showProperty = hasPolicy(['default_list_property_view'])
    const showCompany = hasPolicy(['default_list_company_view'])

    const availableColumnIds = new Set<ReimbursementListColumnId>(
      REIMBURSEMENTS_LIST_CUSTOMIZABLE_COLUMN_OPTIONS.filter((column) => {
        if (column.id === 'property_name') return showProperty
        if (column.id === 'company_name') return showCompany
        if (column.id === 'review_decision') return isLibertyRole
        if (column.id === 'contract_start_date') return isLibertyOrg
        if (column.id === 'adjust_max_movein_date') return isLibertyOrg
        if (column.id === 'access_confirmation') return isLibertyOrg
        if (column.id === 'statement') return isLibertyOrg
        return true
      }).map((column) => column.id),
    )

    return dropdownColumnOrderIds
      .filter((columnId) => !isReimbursementListLockedColumn(columnId))
      .map((columnId) =>
        REIMBURSEMENTS_LIST_COLUMN_OPTIONS.find((column) => column.id === columnId),
      )
      .filter(
        (column): column is (typeof REIMBURSEMENTS_LIST_CUSTOMIZABLE_COLUMN_OPTIONS)[number] =>
          column !== undefined && availableColumnIds.has(column.id),
      )
  }, [dropdownColumnOrderIds, roleName, orgId, hasPolicy])

  const filteredColumnOptions = useMemo(() => {
    const normalizedSearch = columnSearchTerm.trim().toLowerCase()
    if (!normalizedSearch) return availableColumnOptions

    return availableColumnOptions.filter((column) =>
      column.label.toLowerCase().includes(normalizedSearch),
    )
  }, [availableColumnOptions, columnSearchTerm])

  const visibleColumnSet = useMemo(
    () => new Set<ReimbursementListColumnId>(visibleColumnIds),
    [visibleColumnIds],
  )

  const selectedAvailableColumnCount = availableColumnOptions.filter((column) =>
    visibleColumnSet.has(column.id),
  ).length
  const areAllAvailableColumnsSelected =
    selectedAvailableColumnCount === availableColumnOptions.length
  const areNoAvailableColumnsSelected = selectedAvailableColumnCount === 0

  const setVisibleColumns = (columnIds: ReimbursementListColumnId[]) => {
    setColumnSettings((currentSettings) => ({
      ...currentSettings,
      visibleColumnIds: withLockedReimbursementListColumns(
        currentSettings.columnOrderIds.filter((columnId) =>
          columnIds.includes(columnId),
        ),
      ),
    }))
  }

  const toggleColumn = (columnId: ReimbursementListColumnId) => {
    if (isReimbursementListLockedColumn(columnId)) return

    setColumnSettings((currentSettings) => {
      const nextColumnIds = currentSettings.visibleColumnIds.includes(columnId)
        ? currentSettings.visibleColumnIds.filter(
            (currentColumnId) => currentColumnId !== columnId,
          )
        : [...currentSettings.visibleColumnIds, columnId]

      return {
        ...currentSettings,
        visibleColumnIds: withLockedReimbursementListColumns(nextColumnIds),
      }
    })
  }

  const getReorderedColumnIds = (
    currentOrderIds: ReimbursementListColumnId[],
    sourceColumnId: ReimbursementListColumnId,
    targetColumnId: ReimbursementListColumnId,
    placement: 'before' | 'after',
  ) => {
    if (
      sourceColumnId === targetColumnId ||
      isReimbursementListLockedColumn(sourceColumnId) ||
      isReimbursementListLockedColumn(targetColumnId)
    ) {
      return currentOrderIds
    }

    const nextOrderIds = currentOrderIds.filter(
      (columnId) => columnId !== sourceColumnId,
    )
    const targetIndex = nextOrderIds.indexOf(targetColumnId)

    if (targetIndex === -1) return currentOrderIds

    nextOrderIds.splice(
      placement === 'after' ? targetIndex + 1 : targetIndex,
      0,
      sourceColumnId,
    )

    return nextOrderIds
  }

  const handleColumnDragStart = (
    event: DragEvent<HTMLButtonElement>,
    columnId: ReimbursementListColumnId,
  ) => {
    setDraggedColumnId(columnId)
    setDragPreviewColumnOrderIds(columnOrderIds)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', columnId)
    const row = event.currentTarget.closest('[data-column-option-row]')
    if (row instanceof HTMLElement) {
      event.dataTransfer.setDragImage(row, 16, row.offsetHeight / 2)
    }
  }

  const autoScrollColumnsList = (clientY: number) => {
    const listElement = columnsListRef.current
    if (!listElement) return

    const { top, bottom } = listElement.getBoundingClientRect()
    const scrollZone = 24
    const maxScrollStep = 2

    if (clientY < top + scrollZone) {
      const distanceIntoZone = top + scrollZone - clientY
      listElement.scrollBy({
        top: -Math.min(maxScrollStep, Math.max(0.5, distanceIntoZone / 16)),
      })
      return
    }

    if (clientY > bottom - scrollZone) {
      const distanceIntoZone = clientY - (bottom - scrollZone)
      listElement.scrollBy({
        top: Math.min(maxScrollStep, Math.max(0.5, distanceIntoZone / 16)),
      })
    }
  }

  const handleColumnDragOver = (
    event: DragEvent<HTMLDivElement>,
    targetColumnId: ReimbursementListColumnId,
  ) => {
    event.preventDefault()
    autoScrollColumnsList(event.clientY)

    const sourceColumnId =
      (event.dataTransfer.getData('text/plain') as ReimbursementListColumnId) ||
      draggedColumnId

    if (!sourceColumnId || sourceColumnId === targetColumnId) return

    const targetRect = event.currentTarget.getBoundingClientRect()
    const placement =
      event.clientY > targetRect.top + targetRect.height / 2 ? 'after' : 'before'

    setDragPreviewColumnOrderIds((currentOrderIds) =>
      getReorderedColumnIds(
        currentOrderIds ?? columnOrderIds,
        sourceColumnId,
        targetColumnId,
        placement,
      ),
    )
  }

  const handleColumnDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (dragPreviewColumnOrderIds) {
      setColumnSettings((currentSettings) => ({
        ...currentSettings,
        columnOrderIds: dragPreviewColumnOrderIds,
      }))
    }
    setDraggedColumnId(null)
    setDragPreviewColumnOrderIds(null)
  }

  const resetColumnsToDefault = () => {
    setColumnSearchTerm('')
    setColumnSettings({
      visibleColumnIds: DEFAULT_REIMBURSEMENT_COLUMN_IDS,
      columnOrderIds: DEFAULT_REIMBURSEMENT_COLUMN_IDS,
    })
  }

  const [searchDraft, setSearchDraft] = useState<string>(String(filters.q ?? ''))
  useEffect(() => {
    setSearchDraft(String(filters.q ?? ''))
  }, [filters.q])
  const submitSearch = useCallback(() => {
    const trimmed = searchDraft.trim()
    if (trimmed === (filters.q ?? '')) return
    applyFilters({ q: trimmed })
  }, [applyFilters, filters.q, searchDraft])

  /**
   * Auto-apply the toolbar search shortly after the user stops typing, matching
   * the Application module's search box — no Enter/blur required.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      submitSearch()
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, submitSearch])

  // applicant_id query param prefill (Angular routeParams parity).
  // Primary seed happens in useReimbursementsList (URL → q before first fetch).
  // This effect only strips handoff params from the URL; re-applies if the hook
  // had not yet seen them (e.g. roleName still loading on first paint).
  useEffect(() => {
    const applicantIdParam = searchParams.get('applicant_id')?.trim()
    if (!applicantIdParam) return

    const pastCustomer = searchParams.get('past_customer')?.trim()
    const alreadyApplied = String(filters.q ?? '').trim() === applicantIdParam
    if (!alreadyApplied) {
      applyFilters({
        q: applicantIdParam,
        ...(pastCustomer ? { past_customer: pastCustomer } : {}),
      })
    }

    const next = new URLSearchParams(searchParams)
    next.delete('applicant_id')
    next.delete('past_customer')
    setSearchParams(next, { replace: true })
    // intentionally only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAccessConfirmation = useCallback(async (item: ReimbursementListItem) => {
    const appId = item.application_id ?? item.applicant_id
    const key = `policyDecl-${appId}`
    setProcessingMap((m) => ({ ...m, [key]: true }))
    try {
      const resp = await getPolicyDeclarationFiles(appId)
      const files = resp.files ?? []
      if (files.length === 0) {
        showValidationMessage('No files found')
        return
      }
      if (files.length === 1) {
        const streamUrl = files[0]?.stream_url
        if (!streamUrl) {
          showValidationMessage('File URL not available')
          return
        }
        await openServeFileInNewTab(streamUrl)
      } else {
        setPolicyFilesPopup(files)
      }
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessingMap((m) => {
        const next = { ...m }
        delete next[key]
        return next
      })
    }
  }, [])

  const handleStatusClick = useCallback((type: 'claim_status' | 'defaultStatus', values: string[]) => {
    applyFilters({
      claim_status: type === 'claim_status' ? values : [],
      defaultStatus: type === 'defaultStatus' ? values : [],
      page: 1
    })
  }, [applyFilters])

  const handleStatementDownload = useCallback(async (item: ReimbursementListItem) => {
    const appId = item.application_id ?? item.applicant_id
    if (!item.default_statement || appId == null || appId === '') return
    try {
      // Angular default-list: downloadClaimsFile(application_id, default_statement, 'Statement')
      const path = buildStatementPath(appId, 'Statement', String(item.default_statement))
      await openServeFileInNewTab(path)
    } catch (e) {
      showApiError(e)
    }
  }, [])

  const totalPages = Math.max(1, pagination.last)

  return (
    <div
      data-testid="reimbursements-list-page"
      className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12"
    >
      <PageHeader
        title="REIMBURSEMENTS"
        testIdRoot="reimbursements-list-page-header"
      />

      <div
        data-testid="reimbursements-list-main"
        className="mx-auto flex w-full min-w-0 flex-col gap-3 px-3 py-3 sm:gap-4 sm:px-6 sm:py-6"
      >
        <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="relative flex-1 max-w-md">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              data-testid="reimbursements-list-search-input"
              type="text"
              placeholder="Search by resident or applicant"
              className="w-full pl-12 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)] placeholder:text-slate-400"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onBlur={submitSearch}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submitSearch()
                }
              }}
              autoComplete="off"
            />
          </div>

          <div
            data-testid="reimbursements-list-toolbar-actions"
            className="relative flex w-full items-center gap-2 sm:gap-3 md:ml-auto md:w-auto"
          >
            {/* 1. Export & Export All Buttons */}
            <div className="flex items-center gap-2 sm:gap-3 flex-1 md:flex-none">
              <button
                type="button"
                data-testid="reimbursements-list-export-button"
                disabled={
                  items.length === 0 || exportPolling.isProcessing || exportPolling.triggering
                }
                onClick={() => exportPolling.triggerExport(filters, 'export')}
                className="flex-1 md:flex-none inline-flex items-center justify-center rounded-xl bg-[rgb(21_151_255)] hover:bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Export
              </button>
              <button
                type="button"
                data-testid="reimbursements-list-export-all-button"
                disabled={
                  items.length === 0 || exportPolling.isProcessing || exportPolling.triggering
                }
                onClick={() => exportPolling.triggerExport(filters, 'exportAll')}
                className="flex-1 md:flex-none inline-flex items-center justify-center rounded-xl bg-[rgb(21_151_255)] hover:bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Export All
              </button>
            </div>

            {/* 2. Columns Button & Dropdown */}
            <div
              ref={columnsMenuRef}
              className="relative flex-1 md:flex-none"
              data-testid="reimbursements-list-columns-menu"
            >
              <button
                type="button"
                onClick={() => setIsColumnsOpen((isOpen) => !isOpen)}
                data-testid="reimbursements-list-columns-open-button"
                className="w-full md:w-auto flex items-center justify-center gap-2 border border-[rgb(21_151_255)] bg-white text-[rgb(21_151_255)] hover:bg-blue-50 px-4 py-2.5 rounded-xl font-bold shadow-sm transition-all text-sm whitespace-nowrap"
                aria-expanded={isColumnsOpen}
                aria-haspopup="menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="M9 3v18" />
                  <path d="M15 3v18" />
                </svg>
                <span>Columns</span>
                <span
                  className="text-xs font-bold text-[rgb(21_151_255)]"
                  data-testid="reimbursements-list-columns-selected-count"
                >
                  {selectedAvailableColumnCount}/{availableColumnOptions.length}
                </span>
              </button>

              {isColumnsOpen && (
                <div
                  className="absolute right-0 top-[calc(100%+0.5rem)] z-30 w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                  data-testid="reimbursements-list-columns-dropdown"
                  role="menu"
                >
                  <div className="border-b border-slate-100 p-4">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-slate-900">Customize columns</p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          Select columns and drag the handle to reorder.
                        </p>
                      </div>
                      <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-600">
                        {selectedAvailableColumnCount}/{availableColumnOptions.length}
                      </span>
                    </div>
                    <div className="relative">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                        aria-hidden
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                      </svg>
                      <input
                        type="search"
                        value={columnSearchTerm}
                        onChange={(event) => setColumnSearchTerm(event.target.value)}
                        placeholder="Search columns..."
                        className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
                        data-testid="reimbursements-list-columns-search-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
                    <button
                      type="button"
                      className={`rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition ${
                        areAllAvailableColumnsSelected
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                      disabled={areAllAvailableColumnsSelected}
                      onClick={() => setVisibleColumns(DEFAULT_CUSTOMIZABLE_COLUMN_IDS)}
                      data-testid="reimbursements-list-columns-select-all-button"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className={`rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition ${
                        areNoAvailableColumnsSelected
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                      disabled={areNoAvailableColumnsSelected}
                      onClick={() =>
                        setColumnSettings((currentSettings) => ({
                          ...currentSettings,
                          visibleColumnIds: [...REIMBURSEMENTS_LIST_LOCKED_COLUMN_IDS],
                        }))
                      }
                      data-testid="reimbursements-list-columns-clear-all-button"
                    >
                      Clear All
                    </button>
                    <button
                      type="button"
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                      onClick={resetColumnsToDefault}
                      data-testid="reimbursements-list-columns-reset-button"
                    >
                      Reset
                    </button>
                  </div>

                  <div
                    ref={columnsListRef}
                    className="max-h-72 space-y-1 overflow-y-auto p-3"
                    onDragOver={(event) => {
                      event.preventDefault()
                      autoScrollColumnsList(event.clientY)
                    }}
                    onDrop={handleColumnDrop}
                  >
                    {filteredColumnOptions.length > 0 ? (
                      filteredColumnOptions.map((column) => (
                        <div
                          key={column.id}
                          className={`group flex items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-sm font-semibold text-slate-700 transition ${
                            draggedColumnId === column.id
                              ? 'scale-[0.99] border-blue-100 bg-blue-50 opacity-80 shadow-inner'
                              : 'hover:border-slate-100 hover:bg-slate-50'
                          }`}
                          data-column-option-row
                          data-testid={`reimbursements-list-columns-option-${column.id}`}
                          onDragOver={(event) => handleColumnDragOver(event, column.id)}
                          onDrop={handleColumnDrop}
                        >
                          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                            <input
                              type="checkbox"
                              checked={visibleColumnSet.has(column.id)}
                              onChange={() => toggleColumn(column.id)}
                              className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="truncate leading-5">{column.label}</span>
                          </label>
                          <button
                            type="button"
                            draggable
                            className="ml-auto inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 active:cursor-grabbing group-hover:border-slate-300"
                            onDragStart={(event) => handleColumnDragStart(event, column.id)}
                            onDragEnd={() => {
                              setDraggedColumnId(null)
                              setDragPreviewColumnOrderIds(null)
                            }}
                            aria-label={`Drag ${column.label} to reorder`}
                            title="Drag to reorder"
                            data-testid={`reimbursements-list-columns-drag-handle-${column.id}`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <circle cx="9" cy="5" r="1" />
                              <circle cx="9" cy="12" r="1" />
                              <circle cx="9" cy="19" r="1" />
                              <circle cx="15" cy="5" r="1" />
                              <circle cx="15" cy="12" r="1" />
                              <circle cx="15" cy="19" r="1" />
                            </svg>
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-8 text-center text-sm text-slate-500">
                        No columns match your search.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 3. Filters Button */}
            <button
              type="button"
              data-testid="reimbursements-list-filters-button"
              onClick={() => setIsFilterOpen(true)}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50 transition whitespace-nowrap"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span
                  data-testid="reimbursements-list-active-filter-count"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgb(21_151_255)] text-[10px] font-bold text-white"
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {exportPolling.status && (
              <ListExportStatusCard
                status={exportPolling.status}
                isBusy={exportPolling.isProcessing || exportPolling.triggering}
                onDismiss={() => exportPolling.dismissStatus()}
                // z below PageHeader (z-10) so the card can't paint over the profile drawer
                className="absolute top-[calc(100%+0.625rem)] left-0 right-0 z-[5]"
              />
            )}
          </div>
        </div>

        <ReimbursementsFilters
          filters={filters}
          onFiltersChange={applyFilters}
          onReset={resetFilters}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          statusLabelMaps={statusLabelMaps}
        />

        <ReimbursementsTotalsRow
          totals={totals}
          onStatusClick={handleStatusClick}
        />

        <div
          data-testid="reimbursements-list-results-container"
          className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
        >
          <ListPaginationBar
            placement="header"
            currentPage={pagination.current}
            lastPage={totalPages}
            totalRecords={pagination.total}
            perPage={filters.perPage}
            onPageChange={changePage}
            onPerPageChange={changePerPage}
            perPageOptions={PER_PAGE_OPTIONS}
            summaryLayout="angular"
            testIdPrefix="reimbursements-list"
            barDataTestId="reimbursements-list-pagination-header-bar"
          />

          {loading && (
            <div
              data-testid="reimbursements-list-loading"
              className="flex items-center justify-center py-20"
            >
              <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
            </div>
          )}

          {!loading && items.length === 0 && (
            <div
              data-testid="reimbursements-list-empty"
              className="text-center text-sm text-slate-500 py-12"
            >
              No reimbursements match your filters.
            </div>
          )}

          {!loading && items.length > 0 && (
            <div
              ref={resultsScrollRef}
              className="min-w-0 overflow-x-auto touch-scroll"
              data-testid="reimbursements-list-results-scroll"
            >
              {isMobile ? (
                <ReimbursementsGrid
                  items={items}
                  onAccessConfirmation={handleAccessConfirmation}
                  onStatementDownload={handleStatementDownload}
                  processingMap={processingMap}
                  statusLabelMaps={statusLabelMaps}
                />
              ) : (
                <ReimbursementsTable
                  items={items}
                  orderBy={filters.orderBy ?? 'resident_name'}
                  sortBy={filters.sortBy}
                  onSort={sort}
                  onAccessConfirmation={handleAccessConfirmation}
                  onStatementDownload={handleStatementDownload}
                  processingMap={processingMap}
                  visibleColumnIds={visibleColumnIds}
                  orderedColumnIds={columnOrderIds}
                  statusLabelMaps={statusLabelMaps}
                />
              )}
            </div>
          )}

          <ListPaginationBar
            placement="footer"
            currentPage={pagination.current}
            lastPage={totalPages}
            totalRecords={pagination.total}
            perPage={filters.perPage}
            onPageChange={changePage}
            onPerPageChange={changePerPage}
            perPageOptions={PER_PAGE_OPTIONS}
            summaryLayout="angular"
            testIdPrefix="reimbursements-list"
            barDataTestId="reimbursements-list-pagination-footer-bar"
          />

          {!loading && items.length > 0 && !isMobile ? (
            <StickyTableScrollbar
              containerRef={resultsScrollRef}
              data-testid="reimbursements-list-table-sticky-scrollbar"
            />
          ) : null}
        </div>

        <PolicyDeclarationFilesPopup
          isOpen={policyFilesPopup !== null}
          onClose={() => setPolicyFilesPopup(null)}
          files={policyFilesPopup ?? []}
        />
      </div>
    </div>
  )
}
